import type {
  AdminCaptchaSettingsDTO,
  AdminCaptchaSettingsInput,
  AdminDashboardScriptsDTO,
  AdminDashboardScriptsInput,
  AdminUserListItem,
  AdminUserSubscriptionItem,
  AiConfigEntry,
  PlanDTO,
  PlanInput,
  SubscriptionDTO,
  UsageStat,
} from '@bruno/shared';
import { api } from './client';

export const adminListPlans = () => api<{ plans: PlanDTO[] }>('/v1/admin/plans');
export const adminCreatePlan = (body: PlanInput) =>
  api<PlanDTO>('/v1/admin/plans', { method: 'POST', body });
export const adminUpdatePlan = (id: string, body: PlanInput) =>
  api<PlanDTO>(`/v1/admin/plans/${id}`, { method: 'PUT', body });
export const adminArchivePlan = (id: string) =>
  api<void>(`/v1/admin/plans/${id}`, { method: 'DELETE' });
export const adminSyncPlan = (id: string) =>
  api<{ synced: { productId: string; priceId: string } | null }>(
    `/v1/admin/plans/${id}/sync`,
    { method: 'POST' },
  );

export const adminListAiConfigs = () => api<{ configs: AiConfigEntry[] }>('/v1/admin/ai-configs');
export const adminUpsertAiConfig = (key: string, value: unknown, description?: string | null) =>
  api<void>('/v1/admin/ai-configs', {
    method: 'PUT',
    body: { key, value, description },
  });

export const adminListUsers = (q?: string) =>
  api<{ users: AdminUserListItem[] }>(`/v1/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const adminSuspendUser = (id: string) =>
  api<void>(`/v1/admin/users/${id}/suspend`, { method: 'POST' });
export const adminUnsuspendUser = (id: string) =>
  api<void>(`/v1/admin/users/${id}/unsuspend`, { method: 'POST' });
export const adminImpersonate = (id: string) =>
  api<{ accessToken: string }>(`/v1/admin/users/${id}/impersonate`, { method: 'POST' });

export const adminListUserSubscriptions = (userId: string) =>
  api<{ items: AdminUserSubscriptionItem[] }>(`/v1/admin/users/${userId}/subscriptions`);

export const adminSetUserSubscriptionPlan = (
  userId: string,
  websiteId: string,
  planId: string,
) =>
  api<{ subscription: SubscriptionDTO; plan: PlanDTO }>(
    `/v1/admin/users/${userId}/subscriptions/${websiteId}/plan`,
    { method: 'POST', body: { planId } },
  );

export interface AdminOverview {
  userCount: number;
  websiteCount: number;
  conversationCount: number;
  totalTokens: number;
  totalCostCents: number;
}

export const adminOverview = () => api<AdminOverview>('/v1/admin/analytics/overview');
export const adminUsage = (days = 30) =>
  api<{ stats: UsageStat[] }>(`/v1/admin/analytics/usage?days=${days}`);

export interface AuditLogRow {
  id: string;
  action: string;
  target: string | null;
  payload: Record<string, unknown> | null;
  ip: string | null;
  actorEmail: string | null;
  createdAt: string;
}

export const adminAuditLogs = () => api<{ logs: AuditLogRow[] }>('/v1/admin/audit-logs');

export const adminGetCaptcha = () =>
  api<AdminCaptchaSettingsDTO>('/v1/admin/captcha');
export const adminUpdateCaptcha = (body: AdminCaptchaSettingsInput) =>
  api<AdminCaptchaSettingsDTO>('/v1/admin/captcha', { method: 'PUT', body });

export const adminGetDashboardScripts = () =>
  api<AdminDashboardScriptsDTO>('/v1/admin/dashboard-scripts');
export const adminUpdateDashboardScripts = (body: AdminDashboardScriptsInput) =>
  api<AdminDashboardScriptsDTO>('/v1/admin/dashboard-scripts', { method: 'PUT', body });
